import { inject, injectable } from 'inversify';

import type {
  CardPaymentData,
  CreateOrder,
  CreateOrderArgs,
  GetEntitlements,
  GetEntitlementsResponse,
  GetMediaOffers,
  GetOffers,
  GetPaymentMethods,
  GetSubscriptionOffers,
  IAPStore,
  Offer,
  Order,
  Payment,
  PaymentMethod,
  PaymentWithAdyen,
  PaymentWithoutDetails,
  PaymentWithPayPal,
  UpdateOrder,
} from '../../../../types/checkout';
import CheckoutService from '../CheckoutService';
import type { ServiceResponse } from '../../../../types/service';
import { logError } from '../../../logger';

import type {
  CommonResponse,
  GetAccessFeesResponse,
  AccessFee,
  MerchantPaymentMethod,
  GeneratePayPalParameters,
  VoucherDiscountPrice,
  GetItemAccessResponse,
} from './types';
import JWPAPIService from './JWPAPIService';

@injectable()
export default class JWPCheckoutService extends CheckoutService {
  protected readonly cardPaymentProvider = 'stripe';

  protected readonly apiService;

  constructor(@inject(JWPAPIService) apiService: JWPAPIService) {
    super();
    this.apiService = apiService;
  }

  private formatPaymentMethod = (method: MerchantPaymentMethod, cardPaymentProvider: string): PaymentMethod => {
    return {
      id: method.id,
      methodName: method.method_name.toLocaleLowerCase(),
      provider: cardPaymentProvider,
      logoUrl: '',
    } as PaymentMethod;
  };

  private formatEntitlements = (expiresAt: number = 0, accessGranted: boolean = false): ServiceResponse<GetEntitlementsResponse> => {
    return {
      errors: [],
      responseData: {
        accessGranted,
        expiresAt,
      },
    };
  };

  /**
   * Format an offer id for the given access fee (pricing option). For JWP, we need the asset id and
   * access fee id in some cases.
   */
  private formatOfferId(offer: AccessFee) {
    return `${offer.item_id}_${offer.id}`;
  }

  /**
   * Parse the given offer id and extract the asset id.
   */
  private parseOfferId(offerId: string | number) {
    return typeof offerId === 'string' ? parseInt(offerId.split('_')[0]) : offerId;
  }

  private formatOffer = (offer: AccessFee): Offer => {
    return {
      id: offer.id,
      type: ['ppv', 'ppv_custom'].includes(offer.access_type.name) ? 'pass' : 'subscription',
      offerId: this.formatOfferId(offer),
      appleStoreProductId: offer.external_fees?.find((fee) => fee.payment_provider_id === 12)?.external_id || `${offer.item_id}_${offer.id}`,
      googlePlayProductId: offer.external_fees?.find((fee) => fee.payment_provider_id === 14)?.external_id || `${offer.item_id}_${offer.id}`,
      offerPrice: offer.amount,
      offerCurrency: offer.currency,
      customerPriceInclTax: offer.amount,
      customerPriceExclTax: offer.amount,
      customerCurrency: offer.currency,
      offerTitle: offer.description,
      active: true,
      period: offer.access_type.period === 'month' && offer.access_type.quantity === 12 ? 'year' : (offer.access_type.period as 'month' | 'week' | 'year'),
      freePeriods: offer.trial_period ? 1 : 0,
      planSwitchEnabled: offer.item.plan_switch_enabled ?? false,
    } satisfies Offer;
  };

  private formatOrder = (payload: CreateOrderArgs): Order => {
    return {
      id: payload.offer.id!,
      customerId: payload.customerId,
      offerId: payload.offer.offerId,
      totalPrice: payload.offer.customerPriceInclTax,
      priceBreakdown: {
        offerPrice: payload.offer.customerPriceInclTax,
        // @TODO is this correct?
        discountAmount: payload.offer.customerPriceInclTax,
        discountedPrice: payload.offer.customerPriceInclTax,
        customerServiceFee: 0,
        paymentMethodFee: 0,
        taxValue: 0,
      },
      taxRate: 0,
      currency: payload.offer.offerCurrency || 'EUR',
      requiredPaymentDetails: true,
    } as Order;
  };

  createOrder: CreateOrder = async (payload) => {
    return {
      errors: [],
      responseData: {
        message: '',
        order: this.formatOrder(payload),
        success: true,
      },
    };
  };

  getOffers: GetOffers = async (payload) => {
    const offers = await Promise.all(
      payload.offerIds.map(async (offerId) => {
        try {
          const data = await this.apiService.get<GetAccessFeesResponse>(`/v2/items/${this.parseOfferId(offerId)}/access-fees`);

          return data?.map((offer) => this.formatOffer(offer));
        } catch {
          throw new Error('Failed to get offers');
        }
      }),
    );

    return offers.flat();
  };

  getSubscriptionOffers: GetSubscriptionOffers = ({ config, customerCountry }) => {
    const jwpConfig = config.integrations?.jwp;
    const svodOfferIds = jwpConfig?.assetId ? [String(jwpConfig.assetId)] : [];

    return this.getOffers({ offerIds: svodOfferIds, customerCountry });
  };

  getMediaOffers: GetMediaOffers = async ({ mediaProducts, customerCountry }) => {
    const offerIds = mediaProducts.map(({ id }) => id);

    return this.getOffers({ offerIds, customerCountry });
  };

  getPaymentMethods: GetPaymentMethods = async () => {
    try {
      const data = await this.apiService.get<MerchantPaymentMethod[]>('/payments/methods', { withAuthentication: true });
      const paymentMethods: PaymentMethod[] = [];
      data.forEach((method: MerchantPaymentMethod) => {
        if (['card', 'paypal'].includes(method.method_name.toLowerCase())) {
          paymentMethods.push(this.formatPaymentMethod(method, this.cardPaymentProvider));
        }
      });
      return {
        errors: [],
        responseData: {
          message: '',
          paymentMethods,
          status: 1,
        },
      };
    } catch {
      throw new Error('Failed to get payment methods');
    }
  };

  paymentWithPayPal: PaymentWithPayPal = async (payload) => {
    try {
      const data = await this.apiService.post<GeneratePayPalParameters>(
        '/external-payments',
        {
          origin: payload.waitingUrl,
          access_fee: payload.order.id,
          payment_method: 2,
          voucher_code: payload.couponCode,
        },
        { withAuthentication: true },
      );

      if (data?.id) {
        return {
          errors: ['Already have an active access'],
          responseData: {
            redirectUrl: payload.errorUrl,
          },
        };
      }
      return {
        errors: [],
        responseData: {
          redirectUrl: data.endpoint,
        },
      };
    } catch {
      throw new Error('Failed to generate PayPal payment url');
    }
  };

  iFrameCardPayment: PaymentWithAdyen = async () => {
    return {
      errors: [],
      responseData: {} as Payment,
    };
  };

  paymentWithoutDetails: PaymentWithoutDetails = async () => {
    return {
      errors: [],
      responseData: {} as Payment,
    };
  };

  updateOrder: UpdateOrder = async ({ order, couponCode }) => {
    try {
      const data = await this.apiService.post<VoucherDiscountPrice>(
        '/vouchers/discount',
        {
          voucher_code: `${couponCode}`,
          access_fee_id: order.id,
        },
        { withAuthentication: true },
      );

      const discountAmount = order.totalPrice - data.amount;
      const updatedOrder: Order = {
        ...order,
        totalPrice: data.amount,
        priceBreakdown: {
          ...order.priceBreakdown,
          discountAmount,
          discountedPrice: discountAmount,
        },
        discount: {
          applied: true,
          type: 'coupon',
          periods: data.discount_duration,
        },
      };

      return {
        errors: [],
        responseData: {
          message: 'successfully updated',
          order: updatedOrder,
          success: true,
        },
      };
    } catch (error: unknown) {
      if (JWPAPIService.isCommonError(error) && error.response.data.message === 'Voucher not found') {
        throw new Error('Invalid coupon code');
      }

      throw new Error('An unknown error occurred');
    }
  };

  getEntitlements: GetEntitlements = async ({ offerId }) => {
    try {
      const data = await this.apiService.get<GetItemAccessResponse>(`/items/${this.parseOfferId(offerId)}/access`, {
        withAuthentication: true,
      });

      return this.formatEntitlements(data.expires_at, true);
    } catch {
      return this.formatEntitlements();
    }
  };

  directPostCardPayment = async (cardPaymentPayload: CardPaymentData, order: Order, offer: Offer, referrer: string, returnUrl: string) => {
    const payload = {
      number: cardPaymentPayload.cardNumber.replace(/\s/g, ''),
      card_name: cardPaymentPayload.cardholderName,
      exp_month: cardPaymentPayload.cardExpMonth || '',
      exp_year: cardPaymentPayload.cardExpYear || '',
      cvv: cardPaymentPayload.cardCVC,
      access_fee: order.id,
      payment_method: 1,
      voucher_code: cardPaymentPayload.couponCode,
      referrer,
      return_url: returnUrl,
    };

    try {
      if (offer.type === 'subscription') {
        await this.apiService.post<CommonResponse>('/subscriptions', payload, { withAuthentication: true });
      } else {
        await this.apiService.post<CommonResponse>('/payments', payload, { withAuthentication: true });
      }

      return true;
    } catch {
      throw new Error('Failed to make payment');
    }
  };

  getSubscriptionSwitches = undefined;

  getOrder = undefined;

  switchSubscription = undefined;

  getSubscriptionSwitch = undefined;

  createAdyenPaymentSession = undefined;

  initialAdyenPayment = undefined;

  finalizeAdyenPayment = undefined;

  updatePaymentMethodWithPayPal = undefined;

  deletePaymentMethod = undefined;

  addAdyenPaymentDetails = undefined;

  finalizeAdyenPaymentDetails = undefined;

  getOffer = undefined;

  /**
   * VD Private code below
   */
  forwardStoreReceipt = async (store: IAPStore, offer: Offer, receipt: string) => {
    const externalProductId = store === 'apple' ? offer.appleStoreProductId : offer.googlePlayProductId;
    const productId = externalProductId || offer.offerId;

    // product_name must equal the App Store/Google Play subscriptions id
    const payload = { product_name: productId, receipt };

    try {
      if (store === 'google') {
        await this.apiService.post<CommonResponse>('/v2/external-payments/google-play/validate', payload, { withAuthentication: true });
      } else if (store === 'apple') {
        await this.apiService.post<CommonResponse>('/v2/external-payments/apple/validate', payload, { withAuthentication: true });
      }

      return true;
    } catch (error: unknown) {
      logError('JWPCheckoutService', 'Failed to forward receipt', { error });

      return false;
    }
  };
}
